import { useEffect, useState } from "react";
import ZFItemSelector from "../../../../component/ui-component/ZFItemSelector/ZFItemSelector";
import { GraphLegends } from "../../../../component/common/GraphLegend/GraphLegend";
import { legendProperties } from "../../fixtures";
import {
	IOrderListingTableProps,
	IOrderListingConfig,
} from "./OrderListingTable.d";
import { getColumnTemplate, getDetailViewColumn } from "./OrderListingTable.utils";

export const OrderListingTable: React.FC<IOrderListingTableProps> = ({
	headers,
	orderListingConfig,
	pageProperties,
	onRowClick,
	onPaginationChange,
	enableDetailView
}) => {
	const [viewIndex, updateViewIndex] = useState({
		startIndex: 0,
		endIndex: 0,
	});

	const [pageNumberSelector, updatePageNoSelector] = useState({
		header: "PAGE",
		itemList: [],
	});

	const itemsPerPageSelector = {
		header: "ITEMS PER PAGE",
		itemList: [10, 25, 50],
	};

	useEffect(() => {
		setPaginationProps(orderListingConfig);
	}, [orderListingConfig]);

	const setPaginationProps = (orderListingConfig: IOrderListingConfig) => {
		const totalOrders = orderListingConfig.totalRecords;

		setPageNumberList(pageProperties.itemsPerPage, totalOrders);

		calculateViewIndex(
			pageProperties.pageNo,
			pageProperties.itemsPerPage,
			totalOrders,
			orderListingConfig.orderItems.length
		);
	};

	const setPageNumberList = (itemsPerPage: number, totalOrders: number) => {
		let numberOfPagesRequired = Math.max(
			Math.ceil(totalOrders / itemsPerPage),
			1
		);

		const arr = [...Array(numberOfPagesRequired + 1).keys()];
		arr.shift();

		updatePageNoSelector({ ...pageNumberSelector, itemList: arr });
	};

	const calculateViewIndex = (
		pageNumber: number,
		itemsPerPage: number,
		totalOrders: number,
		orderItemsLength: number
	) => {
		if (totalOrders !== 0 && orderItemsLength !== 0) {
			const startIndex = itemsPerPage * (pageNumber - 1) + 1;
			const endIndex =
				startIndex - 1 + Math.min(orderItemsLength, itemsPerPage);
			updateViewIndex({ startIndex, endIndex });
		}
	};

	return (
		<div className="order-listing zf-margin-t-xxxl">
			<div className="view-index" style={{ marginBottom: 20 }}>
				<span style={{ paddingRight: 24 }}>
					Showing {viewIndex.startIndex} to {viewIndex.endIndex} of{" "}
					{orderListingConfig.totalRecords} Orders
				</span>
				Items Per Page:{" "}
				<ZFItemSelector
					items={itemsPerPageSelector}
					activeItem={pageProperties.itemsPerPage}
					onActiveItemChange={(item) => {
						onPaginationChange(item, 1);
					}}
				/>
				Page:{" "}
				<ZFItemSelector
					items={pageNumberSelector}
					activeItem={pageProperties.pageNo}
					onActiveItemChange={(item) => {
						onPaginationChange(pageProperties.itemsPerPage, item);
					}}
				/>
				{legendProperties && (
					<div className="tbl-legend">
						<GraphLegends legends={legendProperties} legendClass="progress-legend" textClass="progress-legend-text"/>
					</div>
				)}
			</div>

			<div className="order-tbl-container">
				<div className="header-row">
					{headers.map((header) => {
						return (
							<div
								key={`header_${header.value}`}
								className={
									"table-column " +
									(header.classes
										? header.classes.join(" ")
										: "")
								}
							>
								{header.value}
							</div>
						);
					})}
					{
						(enableDetailView) && getDetailViewColumn(false, () => {})
					}
				</div>
				<div className="order-tbl-body-container">
					{orderListingConfig &&
						orderListingConfig.orderItems.map((orderItem) => {
							return (
								<div
									key={`row_${orderItem.orderShippableId}`}
									style={{ display: "flex" }}
									className={
										"data-row " +
										(orderItem.rowClasses
											? orderItem.rowClasses.join(" ")
											: "")
									}
								>
									{orderItem.fields.map((field) => {
										return (
											<div
												key={`column_${field.key}`}
												className={
													"table-column" +
													" " +
													(field.classes
														? field.classes.join(
																" "
														  )
														: "")
												}
											>
												{getColumnTemplate(field)}
											</div>
										);
									})}
									{
										(enableDetailView) && getDetailViewColumn(true, onRowClick, orderItem.orderShippableId)
									}
								</div>
							);
						})}
					{orderListingConfig &&
						orderListingConfig.orderItems.length === 0 && (
							<div
								style={{
									textAlign: "center",
									position: "relative",
									top: 100,
								}}
							>
								NO DATA
							</div>
						)}
				</div>
			</div>
		</div>
	);
};
